import {
  Button,
  Input,
  Tab,
  Tabs,
  Textarea,
} from '@heroui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import DragDropUpload from 'components/core/DragDropUpload'
import config from 'config'
import { KNOWLEDGE_BASE_KEYS, useKnowledgeBaseStatus } from 'hooks/useKnowledgeBase'
import { ArrowUp, FileText, Globe } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import ProcessModal from './ProcessModal'
import RawTextModal from './RawTextModal'
import WebScrapingModal from './WebScrapingModal'

export default function UploadSidebar({ onSuccess }) {
  const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
  const [uploadedFile, setUploadedFile] = useState(null)
  const [uploadComplete, setUploadComplete] = useState(false)
  const [openProcess, setOpenProcess] = useState(false)
  const [activeUploadTab, setActiveUploadTab] = useState('upload')
  const [knowledgeBaseId, setKnowledgeBaseId] = useState(null)
  const [openWebScraping, setOpenWebScraping] = useState(false)
  const [openRawText, setOpenRawText] = useState(false)

  const [isUploading, setIsUploading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  })

  const queryClient = useQueryClient()

  const handleFileChange = (files) => {
    const file = files[0]
    if (file && file.size > MAX_FILE_SIZE) {
      toast.error('File size exceeds 10 MB limit')
      return
    }
    setUploadedFile(file)
  }

  // Hook to poll knowledge base status
  const statusQuery = useKnowledgeBaseStatus(knowledgeBaseId, {
    enabled: !!knowledgeBaseId,
    refetchInterval: (data) => {
      const currentStatus = data?.state?.data?.data?.status

      if (currentStatus === 'PROCESSING') {
        return 5000 // Poll every 5 seconds
      }
      return false // Stop polling
    },
    onSuccess: (data) => {
      const status = data?.data?.status

      if (status === 'COMPLETED') {
        setUploadComplete(true)

        // Invalidate knowledge base queries to force cache refresh
        queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASE_KEYS.lists() })

        onSuccess()
        // Don't reset form here - let user decide when to clear it
      }
      else if (status === 'FAILED') {
        toast.error(data?.data?.error_message || 'Failed to process file')
        onSuccess()
        // Don't reset form here - let user decide when to clear it
      }
    },
    onError: (error) => {
      console.error('Failed to check processing status:', error)
      toast.error('Failed to check processing status')
      // Don't reset form here - let user decide when to clear it
    },
  })

  // Second step: Process the uploaded file for knowledge base
  const { mutate: processFile } = useMutation({
    mutationFn: (fileId) => {
      return $fetch('/v1/knowledge/learn', {
        method: 'POST',
        body: {
          type: 'file',
          file_id: fileId,
          name: formData.name || uploadedFile.name,
          description: formData.description,
        },
      })
    },
    onSuccess: ({ data }) => {
      // Set the knowledge base ID to start polling
      setKnowledgeBaseId(data.knowledge_base_id)
      setIsUploading(false) // File upload complete
      // Close the ProcessModal immediately after upload completes
      setOpenProcess(false)
      toast.success('File uploaded successfully! Processing in background.')
      // Refresh the knowledge bases list to show the new item
      onSuccess()
      // Clear the uploaded file to allow new uploads
      setUploadedFile(null)
      setFormData({ name: '', description: '' })
    },
    onError: (error) => {
      console.error(error)
      const errorMessage = error.data?.errors?.[0]?.message
      toast.error(errorMessage ?? 'Failed to process file')
      setIsUploading(false)
      setOpenProcess(false)
    },
  })

  // First step: Upload file to get file ID
  const { mutate: uploadFile } = useMutation({
    mutationFn: (file) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('usage', 'knowledgebase')
      formData.append('filename', file.name ?? `${new Date().toISOString()}.${file.name?.split('.').pop() || 'file'}`)
      return $fetch(config.ENV === 'development' ? 'http://localhost:8787/v1/files/attach' : 'https://api.bland.ai/v1/files/attach', {
        method: 'POST',
        body: formData,
      })
    },
    onSuccess: async ({ data }) => {
      // Once file is uploaded, process it for knowledge base
      processFile(data.id)
    },
    onError: (error) => {
      console.error(error)
      const errorMessage = error.data?.errors?.[0]?.message
      toast.error(errorMessage ?? 'Failed to upload file')
      setIsUploading(false)
      setOpenProcess(false)
    },
  })

  const handleSubmit = async () => {
    if (!uploadedFile) {
      toast.error('Please select a file to upload')
      return
    }

    setIsUploading(true)
    setOpenProcess(true)
    setUploadComplete(false)
    setKnowledgeBaseId(null) // Reset KB ID

    // Start the upload process
    uploadFile(uploadedFile)
  }

  return (
    <div className={`
      flex max-h-screen w-96 flex-col border-l border-gray-200 bg-white
    `}
    >
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-4">
          <div>
            <h2 className="mb-1 text-lg font-semibold text-black">Update Knowledge Base</h2>
            <p className="mb-3 text-sm text-gray-600">
              Add files or URLs such as PDFs, audio, videos, or webpages to help your agents learn from your content.
            </p>
          </div>

          <Tabs
            selectedKey={activeUploadTab}
            onSelectionChange={setActiveUploadTab}
            className="mb-3"
            variant="solid"
            radius="none"
          >
            <Tab key="upload" title="Upload" />
            <Tab key="link" title="Website Link" />
            <Tab key="text" title="Text" />
          </Tabs>

          {/* Background processing indicator */}
          {statusQuery.data?.data?.status === 'PROCESSING' && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 animate-pulse rounded-full bg-blue-600"></div>
                <span className="text-sm text-blue-800">
                  Document processing in background...
                </span>
              </div>
            </div>
          )}

          {activeUploadTab === 'upload'
            ? (
                <div className="w-full space-y-3">
                  <div>
                    <label className="mb-1 block text-sm font-medium">
                      File Name
                      <span className="ml-1 text-gray-400">(Optional)</span>
                    </label>
                    <Input
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter file name"
                      radius="none"
                      size="sm"
                      classNames={{
                        input: 'bg-gray-50',
                        inputWrapper: 'border-gray-200',
                      }}
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium">Description</label>
                    <Textarea
                      value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Enter description"
                      radius="none"
                      size="sm"
                      minRows={2}
                      maxRows={3}
                      classNames={{
                        input: 'bg-gray-50',
                        inputWrapper: 'border-gray-200',
                      }}
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium">File</label>
                    <div className={`
                      rounded-lg border-2 border-dashed border-gray-300 p-4
                      text-center
                    `}
                    >
                      <ArrowUp className="mx-auto mb-1 h-5 w-5 text-gray-400" />
                      <DragDropUpload
                        label="Drag and drop files here or click to upload"
                        onFilesAdded={handleFileChange}
                        uploadedFiles={uploadedFile ? [uploadedFile] : []}
                        multiple={false}
                        accept={{
                          'application/pdf': [],
                          'audio/mp3': [],
                          'audio/mpeg': [],
                          'video/mp4': [],
                          'text/plain': [],
                          'text/csv': [],
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                        [],
                        }}
                      />
                      <p className="mt-1 text-xs text-gray-400">
                        Supported formats: .pdf, .txt, .doc, .docx, .csv.
                        <br />
                        Max File Size: 10 MB
                      </p>
                      <div className={`
                        mt-2 rounded-md border border-blue-200 bg-blue-50 p-2
                      `}
                      >
                        <p className="text-xs text-blue-700">
                          💡
                          {' '}
                          <strong>Tip:</strong>
                          {' '}
                          CSV files provide the best performance for knowledge retrieval
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            : activeUploadTab === 'link'
              ? (
                  <div className="w-full space-y-3">
                    <div className="py-4 text-center">
                      <Globe className="mx-auto mb-3 h-10 w-10 text-gray-400" />
                      <h3 className="mb-1 text-base font-medium text-gray-900">Scrape Website</h3>
                      <p className="mb-4 text-sm text-gray-600">
                        Extract content from websites and add them to your knowledge base.
                        Perfect for documentation, blogs, and other web content.
                      </p>

                      <Button
                        onPress={() => setOpenWebScraping(true)}
                        className={`
                          bg-black text-white
                          hover:bg-gray-800
                        `}
                        radius="none"
                        size="md"
                      >
                        Select Website
                      </Button>
                    </div>

                    <div className="border-t pt-3">
                      <h4 className="mb-1 text-sm font-medium text-gray-900">Features:</h4>
                      <ul className="space-y-0.5 text-sm text-gray-600">
                        <li>• Automatic sitemap discovery</li>
                        <li>• Selective URL scraping</li>
                        <li>• Content extraction & cleaning</li>
                        <li>• Bulk processing</li>
                      </ul>
                    </div>
                  </div>
                )
              : (
                  <div className="w-full space-y-3">
                    <div className="py-4 text-center">
                      <FileText className="mx-auto mb-3 h-10 w-10 text-gray-400" />
                      <h3 className="mb-1 text-base font-medium text-gray-900">Add Raw Text</h3>
                      <p className="mb-4 text-sm text-gray-600">
                        Create a knowledge base from text content you paste or type directly.
                        Perfect for policies, FAQs, documentation, or any text-based information.
                      </p>

                      <Button
                        onPress={() => setOpenRawText(true)}
                        className={`
                          bg-black text-white
                          hover:bg-gray-800
                        `}
                        radius="none"
                        size="md"
                      >
                        Add Text Content
                      </Button>
                    </div>

                    <div className="border-t pt-3">
                      <h4 className="mb-1 text-sm font-medium text-gray-900">Features:</h4>
                      <ul className="space-y-0.5 text-sm text-gray-600">
                        <li>• Direct text input</li>
                        <li>• No file uploads needed</li>
                        <li>• Instant processing</li>
                        <li>• Perfect for copy-paste content</li>
                      </ul>
                    </div>
                  </div>
                )}
        </div>
      </div>

      {/* Fixed upload button at bottom for upload tab */}
      {activeUploadTab === 'upload' && (
        <div className="border-t bg-white p-6">
          <Button
            onPress={handleSubmit}
            className={`
              w-full bg-black text-white
              hover:bg-gray-800
            `}
            color="primary"
            isDisabled={isUploading}
            isLoading={isUploading}
            radius="none"
            size="lg"
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      )}

      {openProcess && (
        <ProcessModal
          open={openProcess}
          setOpen={setOpenProcess}
          uploadComplete={uploadComplete}
          onRefresh={onSuccess}
          mock={false}
        />
      )}

      {openWebScraping && (
        <WebScrapingModal
          open={openWebScraping}
          setOpen={setOpenWebScraping}
          onSuccess={onSuccess}
        />
      )}

      {openRawText && (
        <RawTextModal
          open={openRawText}
          setOpen={setOpenRawText}
          onSuccess={onSuccess}
        />
      )}
    </div>
  )
}
